import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import {
  AuthService,
  CalendarRoomRow,
  CalendarRoomRowApiModel,
  LocationModel,
  LocationsApiResponseModel,
  PagedResponse,
  RoleType,
  RoomApiResponseModel,
  RoomAvailabilityApiResponseModel,
  RoomCalendarEventsModel,
  RoomDetailsApiResponseModel,
  RoomDetailsModel,
  RoomFiltersApiResponseModel,
  RoomFiltersModel,
  RoomFlatModel,
  RoomHistoryApiResponseModel,
  RoomHistoryEntryModel,
  RoomLayoutUploadApiRequestModel,
  RoomLayoutUploadApiResponseModel,
  RoomLayoutUploadModel,
  RoomQueryParams,
  RoomQueryParamsApiModel,
  RoomServicesApiResponseModel,
  RoomServicesModel,
  RoomSettingApiResponseModel,
  RoomSettingModel,
  RoomSettingsApiRequestModel,
  RoomsByEmailApiResponseModel,
  RoomsListApiResponseModel,
  RoomsListModel,
  RoomsModel,
  environment,
} from '@bookly/shared';

import {
  calendarItemsConverter,
  roomCalendarEventsConverter,
  roomHistoryConverter,
} from './rooms.converter';
import { BookingRequestApiService } from '../../booking-request/services/booking-request-api.service';

@Injectable({
  providedIn: 'root',
})
export class RoomsApiService {
  readonly #authService = inject(AuthService);
  readonly #http = inject(HttpClient);
  readonly #bookingRequestApiService = inject(BookingRequestApiService);
  readonly #userCalendarItemsUrl = `${environment.user.roomBookingApi}/calendar/items`;
  readonly #roomsByIdsUrl = environment.shared.roomsByIdsApi;
  readonly #roomsByEmailUrl = environment.admin.roomsByEmailApi;
  readonly #userEmail = this.#authService.user?.email;
  readonly #roomsUrl = environment.shared.roomsApi;
  readonly #roomSettingsUrl = environment.admin.roomSettingsApi;
  readonly #roomServicesUrl = environment.admin.roomServicesApi;
  readonly #roomLayoutsUrl = environment.admin.roomLayoutsApi;
  readonly #roomAvailabilityUrl = environment.shared.roomAvailabilityApi;
  readonly #locationsUrl = environment.shared.locationsApi;
  readonly #filtersUrl = environment.user.roomFiltersApi;
  readonly #userAccessToken = this.#authService.accessToken;

  public getRooms(
    queryParams?: RoomQueryParamsApiModel,
    page = 0,
    pageSize = 10
  ): Observable<PagedResponse<RoomsListModel>> {
    let params = buildParams({ page, pageSize, ...(queryParams ?? {}) });
    if (this.#userAccessToken) {
      params = params.set('userAccessToken', this.#userAccessToken);
    }
    return this.#http.get<PagedResponse<RoomsListApiResponseModel>>(
      this.#roomsUrl,
      { params }
    );
  }

  public getRoomsByRoomIds(roomIds: string[]): Observable<RoomsModel[]> {
    return this.#http.post<RoomApiResponseModel[]>(this.#roomsByIdsUrl, [
      ...roomIds,
    ]);
  }

  public getRoomDetails(roomDexId: string): Observable<RoomDetailsModel> {
    return this.#http.get<RoomDetailsApiResponseModel>(
      this.#roomsUrl + roomDexId
    );
  }

  public getRoomSettings(roomDexId: string): Observable<RoomSettingModel> {
    return this.#http.get<RoomSettingApiResponseModel>(
      this.#roomSettingsUrl + roomDexId
    );
  }

  public getRoomHistory(
    roomDexId: string
  ): Observable<RoomHistoryEntryModel[]> {
    return this.#http
      .get<RoomHistoryApiResponseModel>(
        `${this.#roomSettingsUrl}${roomDexId}/history`
      )
      .pipe(map(response => roomHistoryConverter(response)));
  }

  public getRoomServices(): Observable<RoomServicesModel[]> {
    return this.#http.get<RoomServicesApiResponseModel[]>(
      this.#roomServicesUrl
    );
  }

  public getRoomFilters(): Observable<RoomFiltersModel> {
    return this.#http.get<RoomFiltersApiResponseModel>(this.#filtersUrl);
  }

  public updateRoomSettings(
    roomDexId: string,
    request: RoomSettingsApiRequestModel
  ): Observable<RoomSettingModel> {
    return this.#http.put<RoomSettingApiResponseModel>(
      this.#roomSettingsUrl + roomDexId,
      request
    );
  }

  public getRoomsByEmail(
    roleType: RoleType
  ): Observable<RoomsByEmailApiResponseModel> {
    return this.#http.get<RoomsByEmailApiResponseModel>(
      `${this.#roomsByEmailUrl}${roleType}/${this.#userEmail}`
    );
  }

  public uploadLayout(
    layout: RoomLayoutUploadApiRequestModel
  ): Observable<RoomLayoutUploadModel> {
    return this.#http.post<RoomLayoutUploadApiResponseModel>(
      this.#roomLayoutsUrl,
      layout
    );
  }

  public updateLayout(
    layoutId: string,
    layout: RoomLayoutUploadApiRequestModel
  ): Observable<RoomLayoutUploadModel> {
    return this.#http.put<RoomLayoutUploadApiResponseModel>(
      this.#roomLayoutsUrl + `/${layoutId}`,
      layout
    );
  }

  public uploadLayoutFile(fileUrl: string, file: File): Observable<never> {
    return this.#http.put<never>(fileUrl, file);
  }

  public deleteLayout(layoutId: string): Observable<never> {
    return this.#http.delete<never>(this.#roomLayoutsUrl + '/' + layoutId);
  }

  public getRoomAvailability(
    calendarId: string,
    dateFrom: string,
    dateTo: string,
    semiPrivate: boolean
  ): Observable<RoomCalendarEventsModel[]> {
    let params = new HttpParams()
      .set('to_date', dateTo)
      .set('from_date', dateFrom);
    if (semiPrivate) {
      params = params.set('userAccessToken', this.#userAccessToken as string);
    }
    return this.#http
      .get<RoomAvailabilityApiResponseModel>(
        this.#roomAvailabilityUrl + calendarId,
        {
          params,
        }
      )
      .pipe(map(roomCalendarEventsConverter));
  }

  /**
   * User-facing counterpart of the host calendar: fetches the calendar items
   * (rooms with their events) for the given date range and search criteria.
   * `roomTypes` and `featureIds` are arrays and are appended as one query param
   * per value by {@link BookingRequestApiService.prepareQueryParams}.
   */
  public getUserCalendarItems(
    dateFrom: string,
    dateTo: string,
    criteria: RoomQueryParams = {}
  ): Observable<CalendarRoomRow[]> {
    const params = this.#bookingRequestApiService.prepareQueryParams({
      from: dateFrom,
      to: dateTo,
      ...criteria,
    });
    return this.#http
      .get<CalendarRoomRowApiModel[]>(this.#userCalendarItemsUrl, { params })
      .pipe(
        map(response => calendarItemsConverter(response, dateFrom, dateTo))
      );
  }

  public getLocations(): Observable<LocationModel[]> {
    return this.#http.get<LocationsApiResponseModel[]>(this.#locationsUrl);
  }

  // ToDo add separate endpoint on backend for room searchh
  public getFlatRooms(
    queryParams?: RoomQueryParams
  ): Observable<PagedResponse<RoomFlatModel>> {
    let params = queryParams ? buildParams(queryParams) : new HttpParams();
    params = params.append('pageSize', '100');
    return this.#http.get<PagedResponse<RoomFlatModel>>(this.#roomsUrl, {
      params: params,
    });
  }
}

export function buildParams(queryParams: object): HttpParams {
  let params = new HttpParams();
  Object.entries(queryParams)
    .filter((entry): entry is [string, string] => entry[1] !== undefined)
    .forEach(([key, value]) => {
      params = params.set(key, value);
    });
  return params;
}
